import type { FieldOptions, Operator } from '../../../types/QueryBuilder';

import { inferFieldType } from './fieldType';
import { SINGLE_VALUE_OPERATORS } from './operators';

/**
 * Regex operators take a single regex literal, never a value list: a regex already
 * expresses "any of" via alternation (`a|b`) and its body may legally contain the comma
 * the multi-value path splits on (`/\d{1,3}/`).
 */
export function isRegexOperator(operator: Operator): boolean {
  return operator === '//' || operator === '!//';
}

/** Declared `valueType` wins; otherwise infer from the samples the field was built with. */
function isBooleanField(field: FieldOptions): boolean {
  if (field.valueType) {
    return field.valueType === 'boolean';
  }
  return field.sampleValues.length > 0 && inferFieldType(field.sampleValues) === 'BOOLEAN';
}

/**
 * Whether a filter on this field with this operator can hold several comma-separated values.
 *
 * Requires known field metadata: an unconfigured field's cardinality is unknown, so it stays
 * single-valued rather than silently comma-splitting typed text. Among configured fields,
 * booleans (only true/false), the `context` scope field, comparison operators (the metrics
 * backend 400s on a value set), and regex operators are all single-valued.
 *
 * Shared so the value cell's multiselect and any path that *creates* a filter from typed text
 * agree on when a comma is a separator.
 */
export function fieldAllowsMultipleValues(field: FieldOptions | undefined, operator: Operator): boolean {
  return (
    !!field &&
    !isBooleanField(field) &&
    field.field !== 'context' &&
    !SINGLE_VALUE_OPERATORS.has(operator) &&
    !isRegexOperator(operator)
  );
}
